import { useMemo } from 'react'
import { isResourceOfType, useReadBoards, useReadServices } from '@opallabs/api'
import { useOnceTrue } from '@opallabs/hooks'
import { type FilterOption } from '@opallabs/kuiper/src/FilterBar/queries/types'
import { useLabelFilters } from '@opallabs/kuiper/src/FilterBar/queries/useLabelFilters'
import { useStoryFilters } from '@opallabs/kuiper/src/FilterBar/queries/useStoryFilters'
import {
  buildCalendarFilterCategories,
  CalendarFilterCategoryIds,
  type CalendarFilterCategoryOptions,
} from './categories'
import { type CalendarFilterCategory, type CalendarFilterOption } from './types'
import { buildValueTrees, type CalendarValueTrees } from './valueTree'
import {
  type CalendarFilterValueRef,
  type CalendarLegacyFilterValues,
} from './weekViewSeed'

/** Every option in a tree of shared filter options, depth first. */
const flatten = (option: FilterOption | null | undefined): FilterOption[] =>
  option === null || option === undefined
    ? []
    : [option, ...(option.childOptions ?? []).flatMap(flatten)]

/**
 * Pulls one kind of option out of a shared filter-bar tree, which nests
 * headings, channels, accounts and label sets together and tells them apart by
 * an id prefix.
 *
 * The option's id is its `uuid`, never its `id`. The shared filter bar builds
 * `id` from the v2 numeric primary key (`channel-1`), while calendar items name
 * the same channel by its uuid, so matching on `id` would never find anything.
 * An option with no uuid is dropped rather than offered: it cannot match an
 * item, and a filter that silently empties the calendar is worse than a missing
 * one. Grouping headings are the only options without a uuid of their own, and
 * they are not meant to be matched anyway.
 */
export const optionsWithPrefix = (
  root: FilterOption | null | undefined,
  prefix: string,
  /** `label-` also matches `label-set-`, which is a heading, not a label. */
  excludedPrefix?: string
): CalendarFilterOption[] =>
  flatten(root)
    .filter(
      (option) =>
        option.id.startsWith(prefix) &&
        (excludedPrefix === undefined || !option.id.startsWith(excludedPrefix))
    )
    .flatMap((option) =>
      option.uuid === undefined
        ? []
        : [
            {
              // The shared filter bar sometimes hands a palette *name* where a
              // color belongs ('n1'); a swatch painted with one paints nothing
              // and only holds a blank space in front of the row.
              color: option.color?.startsWith('#') ? option.color : undefined,
              id: option.uuid,
              label: option.title,
            },
          ]
    )

/**
 * The workspace's labels, each under the label set it belongs to.
 *
 * The shared filter bar hands back a tree — one root, a level of label sets,
 * then the labels — and the drawer shows the same two levels. A set is an
 * option as well as the heading its labels sit under, exactly as a channel is:
 * it names itself as its own group, and choosing it means every label in it.
 * Nothing carries a set, so the set only filters through the category's value
 * tree.
 */
export const toLabelOptions = (
  root: FilterOption | null | undefined
): CalendarFilterOption[] =>
  (root?.childOptions ?? []).flatMap((set) => {
    // A set with no uuid cannot be told apart from another one, and a heading
    // that cannot be keyed cannot be drawn. Its labels stay, ungrouped.
    const group =
      set.uuid === undefined ? undefined : { id: set.uuid, label: set.title }

    const labels = optionsWithPrefix(set, 'label-', 'label-set-').map(
      (option) => ({ ...option, group })
    )

    // No colour on the set: a set has none of its own, and a blank swatch would
    // only hold a space in front of the row.
    return group === undefined ? labels : [{ ...group, group }, ...labels]
  })

/**
 * The workspace's stories, by name alone.
 *
 * The shared filter bar hands a story a palette name where a colour would go
 * (`n1`), and a swatch painted with one paints nothing: a blank mark holding a
 * space in front of every row. A story is told apart by its name here.
 */
export const toStoryOptions = (
  root: FilterOption | null | undefined
): CalendarFilterOption[] =>
  optionsWithPrefix(root, 'story-').map(({ id, label }) => ({ id, label }))

/**
 * What each label of the shared filter bar is called here, by the id that bar
 * keys it with (`label-45`). A label set is offered as a row of its own, so it
 * crosses over too.
 */
export const toLabelLegacyValues = (
  root: FilterOption | null | undefined
): Array<[string, CalendarFilterValueRef]> =>
  flatten(root).flatMap((option) =>
    option.uuid === undefined || !option.id.startsWith('label-')
      ? []
      : [
          [
            option.id,
            {
              categoryId: CalendarFilterCategoryIds.label,
              valueId: option.uuid,
            },
          ] as [string, CalendarFilterValueRef],
        ]
  )

type ServiceDocument = Parameters<
  NonNullable<Parameters<typeof useReadServices>[0]['select']>
>[0]

/**
 * The workspace's channels, each with its accounts under it, from the one
 * services fetch the month view already makes for its channel marks and its
 * create form.
 *
 * The shared filter bar has a hook for this, but it reads the same endpoint
 * under a key of its own, so using it here would fetch `/services/v2` twice.
 *
 * A channel is an option as well as the heading its accounts sit under: it
 * names itself as its own group, which is what lets the drawer draw the heading
 * as something a person can choose. Choosing it means every account of the
 * channel, because content carries the channel it was published to.
 *
 * An option with no uuid is dropped: an item names its channel by uuid, so an
 * option without one could never match. Neither kind carries a colour — the
 * channel's own mark says which channel a row belongs to.
 */
export const toChannelAndAccountOptions = (
  document: ServiceDocument
): CalendarFilterOption[] => {
  const accounts = (document.included ?? []).filter(
    isResourceOfType('account', 'V2')
  )

  return document.data.flatMap((service) => {
    const uuid = service.attributes.uuid
    if (uuid === undefined || uuid === null) return []

    const group = { id: uuid, label: service.attributes.name }

    return [
      { ...group, group },
      ...service.relationships.accounts.data.flatMap((reference) => {
        const found = accounts.find((each) => each.id === reference.id)
        const accountUuid = found?.attributes.uuid
        if (
          found === undefined ||
          accountUuid === undefined ||
          accountUuid === null
        )
          return []

        return [{ group, id: accountUuid, label: found.attributes.name }]
      }),
    ]
  })
}

/**
 * What each channel and account of the shared filter bar is called here.
 *
 * The bar keys a channel twice: `channel-3` heads the accounts under it, and
 * `service-3` is the row a person checks to mean "any account of this
 * channel". Both stand for the channel, so both are named here.
 */
export const toChannelAndAccountLegacyValues = (
  document: ServiceDocument
): Array<[string, CalendarFilterValueRef]> => {
  const accounts = (document.included ?? []).filter(
    isResourceOfType('account', 'V2')
  )
  const channels = CalendarFilterCategoryIds.channels

  return document.data.flatMap((service) => {
    const uuid = service.attributes.uuid
    if (uuid === undefined || uuid === null) return []

    return [
      [`channel-${service.id}`, { categoryId: channels, valueId: uuid }],
      [`service-${service.id}`, { categoryId: channels, valueId: uuid }],
      ...service.relationships.accounts.data.flatMap((reference) => {
        const accountUuid = accounts.find((each) => each.id === reference.id)
          ?.attributes.uuid

        return accountUuid === undefined || accountUuid === null
          ? []
          : [
              [
                `account-${reference.id}`,
                { categoryId: channels, valueId: accountUuid },
              ] as [string, CalendarFilterValueRef],
            ]
      }),
    ] as Array<[string, CalendarFilterValueRef]>
  })
}

export type UseWorkspaceFilterOptionsResult = Readonly<{
  categories: CalendarFilterCategory[]
  isLoading: boolean
  /**
   * What the values of the legacy filter bar mean here, for a month starting
   * from the week view's filters. Empty until the lists it reads have arrived.
   */
  legacyFilterValues: CalendarLegacyFilterValues
  /**
   * What sits under what, category by category. The matcher needs it to read a
   * chosen channel or label set as the values the items themselves carry.
   */
  trees: CalendarValueTrees
}>

const toBoardOptions = (
  data: Parameters<
    NonNullable<Parameters<typeof useReadBoards>[0]['select']>
  >[0]
): CalendarFilterOption[] =>
  (data.data ?? []).map((board) => ({
    id: board.id,
    label: board.attributes.title,
  }))

/**
 * The one place in `@opallabs/filters` that fetches. Everything else takes
 * options as data, so a surface with its own sources — a plan tab and its
 * custom fields — can build its category list another way.
 *
 * Channels and accounts are read at once, because the grid names an account on
 * every chip and quickfind searches those names. The rest — labels, stories,
 * boards — are only ever read, so they wait until something asks to read them. Nothing on the grid needs them, and a workspace can hold
 * thousands of stories.
 *
 * @note Option ids are the plain resource ids, without the prefixes the shared
 * filter bar adds (`channel-3` becomes `3`). Items have to carry ids from the
 * same space, so whoever builds the `CalendarItem`s and this hook must agree.
 */
export const useWorkspaceFilterOptions = ({
  areNamedListsWanted,
  isLabelListWanted = false,
  workspaceId,
  workspaceUuid,
}: {
  /**
   * Something that shows these names is open — the filter drawer, or a panel.
   * The lists that only those two read are fetched from here on.
   */
  areNamedListsWanted: boolean
  /** Grouping the grid by label needs label names with the panel still shut. */
  isLabelListWanted?: boolean
  /** The numeric workspace id, as the workspace itself carries it. */
  workspaceId: string
  /** The workspace uuid the v3 endpoints take. */
  workspaceUuid: string
}): UseWorkspaceFilterOptionsResult => {
  const numericWorkspaceId = Number(workspaceId)
  const areListsWanted = useOnceTrue(areNamedListsWanted)
  const areLabelsWanted = useOnceTrue(areNamedListsWanted || isLabelListWanted)

  const channels = useReadServices({
    select: toChannelAndAccountOptions,
    workspaceId,
  })
  // The same fetch read a second way. `/services/v2` is asked for once, and
  // both readings of it are kept apart: the drawer wants options, and only a
  // month adopting the week's filters wants the ids that bar keys them with.
  const channelLegacyValues = useReadServices({
    select: toChannelAndAccountLegacyValues,
    workspaceId,
  })
  const labels = useLabelFilters({
    enabled: areLabelsWanted,
    workspaceId: numericWorkspaceId,
  })
  const stories = useStoryFilters({
    enabled: areListsWanted,
    workspaceId: numericWorkspaceId,
  })
  const boards = useReadBoards({
    enabled: areListsWanted,
    // A board's other fields carry every item on it, each checked against the
    // reader's permissions, which costs seconds on a large workspace.
    query: { fields: { board: 'title' } },
    select: toBoardOptions,
    workspaceUuid,
  })

  const categories = useMemo(() => {
    const options: CalendarFilterCategoryOptions = {
      board: boards.data ?? [],
      channels: channels.data ?? [],
      label: toLabelOptions(labels.data),
      story: toStoryOptions(stories.data),
    }

    return buildCalendarFilterCategories(options)
  }, [boards.data, channels.data, labels.data, stories.data])

  const trees = useMemo(() => buildValueTrees(categories), [categories])

  const legacyFilterValues = useMemo(
    () =>
      new Map([
        ...(channelLegacyValues.data ?? []),
        ...toLabelLegacyValues(labels.data),
      ]),
    [channelLegacyValues.data, labels.data]
  )

  return {
    categories,
    legacyFilterValues,
    isLoading:
      boards.isInitialLoading ||
      channels.isInitialLoading ||
      labels.isInitialLoading ||
      stories.isInitialLoading,
    trees,
  }
}
